import { apiFetch } from './apiClient';

/**
 * Upload API — shapes match UPLOAD_FRONTEND_SPEC.md.
 *
 * Direct-to-blob: `putBlob` PUTs the file straight at the Azure SAS URL using
 * XHR so we get real upload progress events. All other calls go through the
 * normal `apiFetch` + bearer-token path.
 */

/**
 * GET /api/accounts/:accountId/upload-readiness
 */
export const getUploadReadiness = async (accountId) =>
  apiFetch(`/accounts/${accountId}/upload-readiness`);

/**
 * GET /api/auth/:platform/upload-start?accountId=<uuid>
 *
 * Note: the spec doc shows `account_id` but the deployed BE accepts
 * `accountId` (matching the existing `/auth/:platform/start` endpoints).
 *
 * Returns `{ authorize_url }`. Caller redirects the browser to it.
 */
export const startUploadScope = async (platform, accountId) =>
  apiFetch(
    `/auth/${platform}/upload-start?accountId=${encodeURIComponent(accountId)}`
  );

/**
 * POST /api/upload-jobs/blob-url
 * Returns `{ blob_url, blob_path, expires_at }`.
 */
export const getBlobUploadUrl = async ({ accountId, filename, contentType, sizeBytes }) =>
  apiFetch('/upload-jobs/blob-url', {
    method: 'POST',
    body: JSON.stringify({
      account_id: accountId,
      filename,
      content_type: contentType,
      size_bytes: sizeBytes,
    }),
  });

/**
 * PUT the file directly at the Azure Blob SAS URL. Uses XHR because `fetch`
 * does not expose upload progress. `onProgress` receives a 0..1 fraction.
 */
export const putBlob = (blobUrl, file, onProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', blobUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve({ ok: true });
      } else {
        reject(new Error(`Blob upload failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Blob upload network error'));
    xhr.onabort = () => reject(new Error('Blob upload aborted'));
    xhr.send(file);
  });

/**
 * POST /api/upload-jobs
 * Body is forwarded as-is; callers build it in the spec's snake_case shape.
 */
export const createUploadJob = async (body) =>
  apiFetch('/upload-jobs', {
    method: 'POST',
    body: JSON.stringify(body),
  });

/**
 * GET /api/upload-jobs/:jobId
 * Called by the progress poller every ~2s, so skip the global loading spinner.
 */
export const getUploadJob = async (jobId) =>
  apiFetch(`/upload-jobs/${jobId}`, {}, false, false);

/**
 * POST /api/upload-jobs/:jobId/retry
 */
export const retryUploadTargets = async (jobId, targetIds) =>
  apiFetch(`/upload-jobs/${jobId}/retry`, {
    method: 'POST',
    body: JSON.stringify({ target_ids: targetIds }),
  });

/**
 * DELETE /api/upload-jobs/:jobId
 */
export const cancelUploadJob = async (jobId) =>
  apiFetch(`/upload-jobs/${jobId}`, { method: 'DELETE' });
